"use client";

import { ReactElement, forwardRef, useEffect, useRef, useState } from "react";
import { Indicators, Input, Wrapper, Textarea } from "./input-text.styles";
import { FieldContainer } from "../field-container/field-container";
import { TailwindColors } from "@components/common/types/general";
import { Close } from "../../close/close";
import { getComponentColorProp } from "./input-text.utils";

export interface InputTextProps {
    /**
     * ID of the input text.
     */
    id?: string;

    /**
     * Name of the input text.
     */
    name?: string;

    /**
     * Label of the input text.
     */
    label?: string;

    /**
     * Secondary label of the input text. Only applied if `label` isset.
     */
    secondaryLabel?: string;

    /**
     * Description of the input text.
     */
    description?: string;

    /**
     * Placeholder of the input text.
     */
    placeholder?: string;

    /**
     * Hide the label of the input text.
     */
    hideLabel?: boolean | false;

    /**
     * Will open a tooltip on hover the label. Only applied if `label` isset.
     */
    tooltip?: string;

    /**
     * Color of the input text.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * Whether it should be an outline input text or not.
     */
    outline?: boolean | false;

    /**
     * By default, an outline input text will get a background color on focus. With this prop you can define whether it should keep the outline or not.
     */
    outlineKeepOnHover?: boolean | false;

    /**
     * If `true`, a `textarea` element is rendered.
     */
    multiline?: boolean;

    /**
     * Minimal number of rows. Will only be applied if `multiline` is `true`.
     */
    minRows?: number;

    /**
     * Maximum number of rows. Will only be applied if `multiline` is `true`.
     */
    maxRows?: number;

    /**
     * Will make the input text resizeable. Will only be applied if `multiline` is `true`.
     */
    resize?: "both" | "horizontal" | "vertical" | "none";

    /**
     * Display a bottom border so that the input text looks like underlined.
     */
    underline?: boolean | false;

    /**
     * Border radius of the input text.
     */
    rounded?: "md" | "lg" | "full" | "l-md" | "r-md" | "l-lg" | "r-lg" | "l-full" | "r-full";

    /**
     * Type of the input text.
     */
    type?: "date" | "datetime-local" | "email" | "month" | "number" | "password" | "search" | "tel" | "text" | "time" | "url" | "week";

    /**
     * A block input text will get 100% width relative to the parent container.
     */
    block?: boolean | false;

    /**
     * Size of the input text.
     */
    size?: "xs" | "sm" | "lg";

    /**
     * Will disable the input text.
     */
    disabled?: boolean | false;

    /**
     * Will display the input text with shadow.
     */
    shadow?: boolean | false;

    /**
     * Mark the input text as required.
     */
    required?: boolean | false;

    /**
     * Set a fixed width to the input text.
     */
    length?: "200" | "250" | "300" | number;

    /**
     * Will make border and background transparent.
     */
    clean?: boolean | false;

    /**
     * Apply tabindex attribute in the dom.
     */
    tabIndex?: number;

    /**
     * A callback function which will be fired on change.
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

    /**
     * A callback function which will be fired on focus.
     */
    onFocus?: (event: React.FocusEvent<HTMLElement, Element>) => void;

    /**
     * A callback function which will be fired on blur.
     */
    onBlur?: (event: React.FocusEvent<HTMLElement, Element>) => void;

    /**
     * A callback function which will be fired on key down.
     */
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

    /**
     * A callback function which will be fired on click the cross icon. Only applied if `isClearable` is true.
     */
    onClear?: () => void;

    /**
     * Value of the input text.
     */
    value?: string;

    /**
     * Will show a cross icon and trigger the `onClear` function if it's set.
     */
    isClearable?: boolean | false;

    /**
     * Error message.
     */
    error?: string;

    /**
     * Left indicator will show before the input text.
     */
    leftIndicator?: ReactElement | string;

    /**
     * Right indicator will show after the input text.
     */
    rightIndicator?: ReactElement | string;

    /**
     * Class name of the input text.
     */
    className?: string;
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
    (
        {
            onChange,
            onBlur,
            onFocus,
            onClear,
            onKeyDown,
            value,
            id,
            name,
            label,
            secondaryLabel,
            description,
            tooltip,
            required,
            hideLabel,
            error,
            color,
            block,
            outline,
            minRows = 1,
            maxRows = 10,
            type = "text",
            resize = "none",
            multiline,
            outlineKeepOnHover,
            underline,
            rounded,
            size,
            disabled,
            shadow,
            length,
            placeholder,
            clean,
            tabIndex,
            leftIndicator,
            rightIndicator,
            isClearable,
            className,
        },
        ref,
    ) => {
        const textareaRef = useRef<HTMLTextAreaElement>(null);
        const [isFocused, setIsFocused] = useState<boolean>(false);

        useEffect(() => {
            if (textareaRef) {
                adjustTextareaHeight();
            }
        }, [value]);

        function onChangeHandler(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
            onChange?.(event);
            adjustTextareaHeight();
        }

        function onFocusHandler(event: React.FocusEvent<HTMLElement, Element>) {
            setIsFocused(true);
            onFocus?.(event);
        }

        function onBlurHandler(event: React.FocusEvent<HTMLElement, Element>) {
            setIsFocused(false);
            onBlur?.(event);
        }

        function adjustTextareaHeight() {
            const textarea = textareaRef.current;
            if (textarea && resize === "none") {
                textarea.style.height = "0px";
                const scrollHeight = textarea.scrollHeight;
                const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
                const minHeight = lineHeight * (minRows < 1 ? 1 : minRows);
                const maxHeight = lineHeight * (maxRows < 1 ? 1 : maxRows);
                const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
                textarea.style.height = `${newHeight}px`;
            }
        }

        return (
            <FieldContainer
                id={id}
                label={label}
                secondaryLabel={secondaryLabel}
                description={description}
                hideLabel={hideLabel}
                required={required}
                tooltip={tooltip}
                error={error}
                className={className}
            >
                <Wrapper
                    color={color}
                    outline={outline}
                    outlineKeepOnHover={outlineKeepOnHover}
                    underline={underline}
                    rounded={rounded}
                    isFocused={isFocused}
                    disabled={disabled}
                    shadow={shadow}
                    length={length}
                    size={size}
                    clean={clean}
                    error={error}
                    block={block}
                >
                    {leftIndicator && <Indicators>{leftIndicator}</Indicators>}
                    {multiline ? (
                        <Textarea
                            onChange={onChangeHandler}
                            onFocus={(event: React.FocusEvent<HTMLElement, Element>) => onFocusHandler(event)}
                            onBlur={(event: React.FocusEvent<HTMLElement, Element>) => onBlurHandler(event)}
                            onKeyDown={onKeyDown}
                            value={value}
                            id={id}
                            name={name}
                            size={size}
                            placeholder={placeholder}
                            disabled={disabled}
                            tabIndex={tabIndex}
                            minRows={minRows}
                            resize={resize}
                            ref={textareaRef}
                        />
                    ) : (
                        <Input
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(event)}
                            onFocus={(event: React.FocusEvent<HTMLElement, Element>) => onFocusHandler(event)}
                            onBlur={(event: React.FocusEvent<HTMLElement, Element>) => onBlurHandler(event)}
                            onKeyDown={onKeyDown}
                            value={value}
                            id={id}
                            name={name}
                            size={size}
                            type={type}
                            placeholder={placeholder}
                            disabled={disabled}
                            tabIndex={tabIndex}
                            ref={ref}
                        />
                    )}
                    {(rightIndicator || isClearable) && (
                        <Indicators>
                            {isClearable && (
                                <Close
                                    size="sm"
                                    background={false}
                                    color={getComponentColorProp(outline, underline, outlineKeepOnHover, color, clean)}
                                    onClick={onClear}
                                />
                            )}
                            {rightIndicator && rightIndicator}
                        </Indicators>
                    )}
                </Wrapper>
            </FieldContainer>
        );
    },
);

InputText.displayName = "InputText";
